// The admin lesson editor's state (plan R1 §1.1, §1.2, §2.2).
//
// The course is read from GET /v1/courses/admin/:id (full, drafts included,
// never the public paywalled read). Every change is ONE targeted request
// addressed by section index / lesson uuid with the course's `sectionsVersion`;
// its answer (the whole new course view) replaces the screen state. A move is
// shown at once and rolled back to the exact previous course if it fails; every
// failure is shown in that section (and in a snackbar). Only one request runs
// at a time: a click while one is saving sends nothing.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { Course, Item, Section } from 'src/@types/Course';
import { lessonUuids, moveUuid, withSectionOrder } from './lessonOrder';
import {
  LessonError,
  courseLessonsApi,
  lessonErrorOf,
  lessonPayload,
  sectionPayload,
  shouldReload,
} from './courseLessonsApi';

export type SectionStatus = { saving?: boolean; saved?: boolean; error?: string };

export const ORDER_NOT_SAVED_TEXT = 'The new order was not saved.';
const LESSON_NOT_SAVED_TEXT = 'The lesson was not saved.';
const LESSON_NOT_DELETED_TEXT = 'The lesson was not deleted.';
const SECTION_NOT_SAVED_TEXT = 'The section was not saved.';
const SECTION_NOT_DELETED_TEXT = 'The section was not deleted.';
const REPAIR_FAILED_TEXT = 'The lesson problems were not fixed.';

type Write = {
  // The section the result is shown in; null = above the sections.
  sectionIndex: number | null;
  failText: string;
  request: (expectedVersion: string) => Promise<Course>;
  // Shown at once, rolled back on failure.
  optimistic?: Course;
  // Only "Fix lesson problems" may run while repairs are needed.
  allowWhileRepairNeeded?: boolean;
  successText?: string;
};

export const useCourseView = () => {
  const params = useParams();
  const courseId = params?.id as string;
  const { enqueueSnackbar } = useSnackbar();

  const [currentCourse, setCurrentCourseState] = useState<Course | undefined>();
  const courseRef = useRef<Course | undefined>();
  const setCurrentCourse = useCallback((course: Course | undefined) => {
    courseRef.current = course;
    setCurrentCourseState(course);
  }, []);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<LessonError | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [sectionStatus, setSectionStatus] = useState<Record<number, SectionStatus>>({});
  const [saving, setSaving] = useState(false);
  const pendingRef = useRef(false);

  // Section modal: `editingSection` is the index of the section being edited
  // (null = a new section). Lesson modal: the target section and, on an edit,
  // the lesson (addressed by its uuid, never by a table row index).
  const [sectionModalOpen, SetSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [lessonSection, setLessonSection] = useState<number | null>(null);
  const [currentItem, setCurrentItem] = useState<{ item: Item; uuid: string } | undefined>();

  const readOnly = !!currentCourse?.partnerOwned;
  const repairNeeded = !!currentCourse?.repairs?.needed;

  const setStatus = (sectionIndex: number | null, status: SectionStatus) => {
    if (sectionIndex === null) return;
    setSectionStatus((prev) => ({ ...prev, [sectionIndex]: status }));
  };

  const reload = useCallback(async () => {
    try {
      const course = await courseLessonsApi.get(courseId);
      setCurrentCourse(course);
      setLoadError(null);
    } catch (err) {
      const error = lessonErrorOf(err);
      if (courseRef.current) setPageError(`The course could not be re-read. ${error.text}`);
      else setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [courseId, setCurrentCourse]);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    setSectionStatus({});
    setPageError(null);
    reload();
  }, [reload]);

  const retry = () => {
    setLoading(true);
    setLoadError(null);
    reload();
  };

  const runWrite = async ({
    sectionIndex,
    failText,
    request,
    optimistic,
    allowWhileRepairNeeded = false,
    successText,
  }: Write): Promise<boolean> => {
    const before = courseRef.current;
    if (pendingRef.current || !before || before.partnerOwned) return false;
    if (before.repairs?.needed && !allowWhileRepairNeeded) return false;
    pendingRef.current = true;
    setSaving(true);
    setStatus(sectionIndex, { saving: true });
    if (sectionIndex === null) setPageError(null);
    if (optimistic) setCurrentCourse(optimistic);
    try {
      const next = await request(before.sectionsVersion ?? '');
      setCurrentCourse(next);
      setStatus(sectionIndex, { saved: true });
      if (successText) enqueueSnackbar(successText);
      return true;
    } catch (err) {
      const error = lessonErrorOf(err, before);
      const text = `${failText} ${error.text}`;
      // Roll back to the exact course shown before the change.
      setCurrentCourse(before);
      if (sectionIndex === null) setPageError(text);
      else setStatus(sectionIndex, { error: text });
      enqueueSnackbar(text, { variant: 'error' });
      if (shouldReload(error)) await reload();
      return false;
    } finally {
      pendingRef.current = false;
      setSaving(false);
    }
  };

  // ↑ / ↓ — section and lesson come from the click, the list from the latest
  // course (never a section remembered from an earlier render).
  const moveLesson = (sectionIndex: number, uuid: string, delta: number) => {
    const course = courseRef.current;
    if (!course || pendingRef.current) return Promise.resolve(false);
    const next = moveUuid(lessonUuids(course.sections?.[sectionIndex]), uuid, delta);
    const optimistic = next ? withSectionOrder(course, sectionIndex, next) : null;
    if (!next || !optimistic) return Promise.resolve(false);
    return runWrite({
      sectionIndex,
      failText: ORDER_NOT_SAVED_TEXT,
      optimistic,
      request: (version) => courseLessonsApi.reorder(courseId, sectionIndex, next, version),
    });
  };

  const onDeleteItem = (sectionIndex: number, uuid: string) =>
    runWrite({
      sectionIndex,
      failText: LESSON_NOT_DELETED_TEXT,
      successText: 'Lesson deleted',
      request: (version) => courseLessonsApi.deleteLesson(courseId, uuid, version),
    });

  const onDeleteSection = async (sectionIndex: number) => {
    const ok = await runWrite({
      sectionIndex,
      failText: SECTION_NOT_DELETED_TEXT,
      successText: 'Section deleted',
      request: (version) => courseLessonsApi.deleteSection(courseId, sectionIndex, version),
    });
    // The sections after it moved up one place: their old messages no longer fit.
    if (ok) setSectionStatus({});
    return ok;
  };

  const onSubmitNewSection = async (data: Partial<Section>) => {
    const sectionIndex = editingSection;
    const section = sectionPayload(data);
    const ok = await runWrite({
      sectionIndex,
      failText: SECTION_NOT_SAVED_TEXT,
      successText: sectionIndex === null ? 'Section created' : 'Section saved',
      request: (version) =>
        sectionIndex === null
          ? courseLessonsApi.addSection(courseId, section, version)
          : courseLessonsApi.updateSection(courseId, sectionIndex, section, version),
    });
    if (ok) {
      SetSectionModalOpen(false);
      setEditingSection(null);
    }
    return ok;
  };

  const closeLessonModal = () => {
    setModalOpen(false);
    setCurrentItem(undefined);
    setModalStep(0);
  };

  // Create: the server mints the uuid. Edit: PATCH by the lesson's uuid; the
  // server merges the form onto the stored lesson, so the uuid never changes.
  const onSubmitNewSectionItem = async (data: Item) => {
    const editing = currentItem;
    const sectionIndex = lessonSection;
    if (!editing && sectionIndex === null) return false;
    const lesson = lessonPayload(data);
    const ok = await runWrite({
      sectionIndex,
      failText: LESSON_NOT_SAVED_TEXT,
      successText: editing ? 'Lesson saved' : 'Lesson created',
      request: (version) =>
        editing
          ? courseLessonsApi.updateLesson(courseId, editing.uuid, lesson, version)
          : courseLessonsApi.addLesson(courseId, sectionIndex as number, lesson, version),
    });
    if (ok) closeLessonModal();
    return ok;
  };

  const repairLessons = () =>
    runWrite({
      sectionIndex: null,
      failText: REPAIR_FAILED_TEXT,
      successText: 'Lesson problems fixed',
      allowWhileRepairNeeded: true,
      request: (version) => courseLessonsApi.repair(courseId, version),
    });

  const openNewSection = () => {
    setEditingSection(null);
    SetSectionModalOpen(true);
  };

  const openEditSection = (sectionIndex: number) => {
    setEditingSection(sectionIndex);
    SetSectionModalOpen(true);
  };

  const closeSectionModal = () => {
    SetSectionModalOpen(false);
    setEditingSection(null);
  };

  const openNewLesson = (sectionIndex: number) => {
    setLessonSection(sectionIndex);
    setCurrentItem(undefined);
    setModalStep(0);
    setModalOpen(true);
  };

  const openEditLesson = (sectionIndex: number, uuid: string) => {
    const item = (courseRef.current?.sections?.[sectionIndex]?.items ?? []).find(
      (it) => it?.uuid === uuid
    );
    if (!item) return;
    setLessonSection(sectionIndex);
    setCurrentItem({ item, uuid });
    setModalOpen(true);
  };

  const clearSectionError = (sectionIndex: number) => setStatus(sectionIndex, {});

  return {
    currentCourse,
    loading,
    loadError,
    retry,
    pageError,
    setPageError,
    sectionStatus,
    clearSectionError,
    saving,
    readOnly,
    repairNeeded,
    // sections
    sectionModalOpen,
    editingSection,
    currentSection: editingSection === null ? undefined : currentCourse?.sections?.[editingSection],
    openNewSection,
    openEditSection,
    closeSectionModal,
    onSubmitNewSection,
    onDeleteSection,
    // lessons
    modalOpen,
    modalStep,
    setModalStep,
    currentItem,
    openNewLesson,
    openEditLesson,
    closeLessonModal,
    onSubmitNewSectionItem,
    onDeleteItem,
    moveLesson,
    repairLessons,
  };
};
